import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Zap,
  Star,
  Check,
  X,
  CreditCard,
  Smartphone,
  Bitcoin,
  Lock,
  Award,
  Trophy,
  Timer,
  Hand,
} from "lucide-react";
import PageLayout from "../components/ui/PageLayout";
import { CreatePayment } from "../utils/PiIntegration";
import { useDispatch, useSelector } from "react-redux";
import {
  selectUserGlobalData,
  updateUserData,
} from "../store/dreamworldSlice";
import dreamWorldProvider from "../DreamWorldProvider";

const BuyFluxPage = () => {
  const dispatch = useDispatch();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  interface FluxPackage {
    id: number;
    flux: number;
    price: number;
    bonus: number;
    popular: boolean;
    badge: string | null;
    color: string;
    icon: React.ReactNode;
  }

  const userGlobalData = useSelector(selectUserGlobalData);
  const [selectedPackage, setSelectedPackage] = useState<FluxPackage | null>(
    null
  );
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState("Pi Wallet");
  const [timeLeft, setTimeLeft] = useState(30 * 60); // 30 minutes in seconds

  useEffect(() => {
    if (timeLeft === 0) return;

    const timer = setInterval(() => {
      setTimeLeft(timeLeft - 1);
    }, 1000);

    return () => clearInterval(timer);
  }, [timeLeft]);

  const formatTime = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes.toString().padStart(2, "0")}:${remainingSeconds
      .toString()
      .padStart(2, "0")}`;
  };

  const packages: FluxPackage[] = [
    {
      id: 1,
      flux: 100,
      price: 5,
      bonus: 0,
      popular: false,
      badge: null,
      color: "from-blue-500 to-cyan-500",
      icon: <Zap className="w-8 h-8 text-white" />,
    },
    {
      id: 2,
      flux: 500,
      price: 15,
      bonus: 50,
      popular: false,
      badge: "🔥 Popular",
      color: "from-purple-500 to-pink-500",
      icon: <Star className="w-8 h-8 text-white" />,
    },
    {
      id: 3,
      flux: 1500,
      price: 30,
      bonus: 150,
      popular: false,
      badge: "+15% Bonus",
      color: "from-orange-500 to-red-500",
      icon: <Award className="w-8 h-8 text-white" />,
    },
    {
      id: 4,
      flux: 5000,
      price: 80,
      bonus: 1000,
      popular: true,
      badge: "💎 Best Value",
      color: "from-yellow-400 to-orange-400",
      icon: <Trophy className="w-8 h-8 text-white" />,
    },
  ];

  const paymentMethods = [
    {
      id: "Pi Wallet",
      name: "Pi native wallet",
      icon: <CreditCard className="w-5 h-5" />,
    },
    {
      id: "Third Party Wallet",
      name: "Third Party Wallet",
      icon: (
        <div className="w-5 h-5 bg-blue-600 rounded text-white text-xs flex items-center justify-center font-bold">
          P
        </div>
      ),
    },
  ];

  const handleBuyPackage = (pkg: FluxPackage) => {
    setSelectedPackage(pkg);
    setShowPaymentModal(true);
  };

  const handlePayment = async () => {
    if (selectedPackage && userGlobalData) {
      try {
        await CreatePayment(
          userGlobalData.user.uid,
          selectedPackage.price,
          "buy_flux",
          async () => {
            setShowPaymentModal(false);
            const currentFlux = userGlobalData.userData.flux || 0;
            const purchasedFlux =
              selectedPackage.flux + selectedPackage.bonus;
            const newFlux = currentFlux + purchasedFlux;

            // Update Redux store
            dispatch(updateUserData({ flux: newFlux }));

            // Update database
            await dreamWorldProvider.updateUserData(userGlobalData.user.uid, {
              flux: newFlux,
            });

            // You can add a success notification here, e.g., using a toast library
            alert("Payment successful!");
          }
        );
      } catch (error) {
        // Handle payment error, e.g., show an error message
        console.error("Payment failed:", error);
        alert("Payment failed. Please try again.");
      }
    }
  };

  return (
    <div className="min-h-screen bg-gray-900 text-white">
      <PageLayout>
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="text-center mb-12"
        >
          <h1 className="text-4xl md:text-5xl font-extrabold mb-2 bg-gradient-to-r from-purple-400 to-pink-600 bg-clip-text text-transparent">
            Supercharge Your Creativity
          </h1>
          <p className="text-lg text-gray-400">
            Join thousands of creators who are making amazing content with Flux.
          </p>
        </motion.div>

        {/* Limited Time Offer */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2, duration: 0.5 }}
          className="bg-gradient-to-r from-yellow-400 via-red-500 to-pink-500 p-4 rounded-xl mb-12 text-center"
        >
          <div className="flex items-center justify-center space-x-4">
            <div className="flex items-center space-x-2">
              <Timer className="w-6 h-6" />
              <p className="font-bold text-lg">LIMITED TIME OFFER</p>
            </div>
            <p className="font-semibold text-lg">
              Offer ends in: {formatTime(timeLeft)}
            </p>
          </div>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 items-start">
          {packages.map((pkg, index) => (
            <motion.div
              key={pkg.id}
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 + index * 0.1 }}
              whileHover={{ y: -5 }}
              className={`bg-gray-800 rounded-2xl border ${
                pkg.popular
                  ? "border-purple-500 shadow-lg shadow-purple-500/20"
                  : "border-gray-700"
              } transition-all duration-300 flex flex-col h-full`}
              onClick={() => handleBuyPackage(pkg)}
            >
              {pkg.badge && (
                <div
                  className={`text-sm font-bold text-center py-2 rounded-t-2xl ${
                    pkg.popular
                      ? "bg-purple-500"
                      : "bg-gradient-to-r " + pkg.color
                  }`}
                >
                  {pkg.badge}
                </div>
              )}

              <div className="p-8 flex flex-col flex-grow">
                <div className="text-center mb-6">
                  <div className="flex justify-center mb-4">{pkg.icon}</div>
                  <h3 className="text-2xl font-bold mb-1">
                    {pkg.flux.toLocaleString()} Flux
                  </h3>
                  {pkg.bonus > 0 && (
                    <p className="text-green-400 font-semibold">
                      + {pkg.bonus.toLocaleString()} Bonus
                    </p>
                  )}
                </div>

                <ul className="text-gray-400 space-y-3 mb-8 flex-grow">
                  <li className="flex items-center">
                    <Check className="w-5 h-5 text-green-500 mr-2" />
                    <span>Unlimited AI Generations</span>
                  </li>
                  <li className="flex items-center">
                    <Check className="w-5 h-5 text-green-500 mr-2" />
                    <span>Unlock All Premium Styles</span>
                  </li>
                  <li className="flex items-center">
                    <Check className="w-5 h-5 text-green-500 mr-2" />
                    <span>Win Exclusive Prizes</span>
                  </li>
                </ul>

                <div className="text-center mt-auto">
                  <div className="mb-4">
                    <span className="text-4xl font-extrabold">
                      {pkg.price} Pi
                    </span>
                    <span className="text-gray-500">/ one-time</span>
                  </div>
                  <button
                    className={`w-full font-bold py-3 rounded-lg transition-all duration-300 ${
                      pkg.popular
                        ? "bg-purple-600 hover:bg-purple-700"
                        : "bg-gray-700 hover:bg-gray-600"
                    }`}
                  >
                    Purchase Now
                  </button>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </PageLayout>

      {/* Payment Modal */}
      <AnimatePresence>
        {showPaymentModal && selectedPackage && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => setShowPaymentModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-gray-800 rounded-3xl max-w-md w-full p-8 border border-gray-700 relative"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                onClick={() => setShowPaymentModal(false)}
                className="absolute top-4 right-4 text-gray-400 hover:text-white transition-colors"
              >
                <X className="w-6 h-6" />
              </button>

              <div className="text-center mb-8">
                <div
                  className={`inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r ${selectedPackage.color} rounded-2xl mb-4`}
                >
                  <Zap className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-white mb-2">
                  Purchase Flux
                </h3>
                <div className="text-gray-300">
                  {selectedPackage.flux.toLocaleString()} Flux
                  {selectedPackage.bonus > 0 && (
                    <span className="text-green-400">
                      {" "}
                      + {selectedPackage.bonus} Bonus
                    </span>
                  )}
                </div>
                <div className="text-lg font-bold text-white mt-2">
                  {selectedPackage.price} Pi
                </div>
              </div>

              {/* Payment Methods */}
              <div className="mb-6">
                <h4 className="text-white font-semibold mb-4">
                  Payment Method
                </h4>
                <div className="space-y-2">
                  {paymentMethods.map((method) => (
                    <button
                      key={method.id}
                      // onClick={() => setSelectedPayment(method.id)}
                      className={`w-full flex items-center gap-3 p-4 rounded-xl border transition-all ${
                        selectedPayment === method.id
                          ? "border-purple-500 bg-purple-500/10"
                          : "border-gray-600 hover:border-gray-500"
                      }`}
                    >
                      {method.icon}
                      <span className="text-white">{method.name}</span>
                      {selectedPayment === method.id && (
                        <Check className="w-5 h-5 text-purple-400 ml-auto" />
                      )}
                    </button>
                  ))}
                </div>
              </div>

              {/* Purchase Button */}
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={handlePayment}
                className={`w-full bg-gradient-to-r ${selectedPackage.color} text-white font-bold py-4 rounded-2xl shadow-lg hover:shadow-xl transition-all`}
              >
                Complete Purchase
              </motion.button>

              <div className="text-center mt-4">
                <p className="text-gray-400 text-sm flex items-center justify-center gap-1">
                  <Lock className="w-4 h-4" />
                  Secured by 256-bit encryption
                </p>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default BuyFluxPage;
